import { useCallback, useEffect, useState } from 'react';
import { css } from 'shared/utils';
import { debounce } from 'shared/utils/debounce';
/**
 * Determine if something must be scaled based on the size of a container HTMLElement.
 */
export function useScale(scaleChecker, containerRef, props) {
    const { inEditor } = props;
    // `useScale` is only ready to operate if one of these is true:
    // 1. We are in-editor
    // 2. We have received a `renderRequested` event.
    const [isReady, setIsReady] = useState(false);
    // // For Debuggging: This is just here to show when `renderRequested` fires.
    // useEffect(() => {
    //     const listener = () => debug('%crenderRequested', 'color: teal');
    //     document.addEventListener('renderRequested', listener);
    //     return () => {
    //         document.removeEventListener('renderRequested', listener);
    //     };
    // }, []);
    const [scale, setScale] = useState(undefined);
    const checkForOverflow = useCallback(() => {
        // debug('useScale: %ccheckOverflow()', 'color: yellow');
        if (!containerRef.current) {
            return;
        }
        const updatedScale = scaleChecker(containerRef.current, props);
        if (scale !== updatedScale) {
            setScale(updatedScale);
        }
    }, [containerRef, props, scale, scaleChecker]);
    // Re-check for overflow when the container HTMLElement is resized or we receive
    // a `renderRequested` event. The `renderRequested` event can be fired, for example:
    //
    //   - When 100% of our assets are loaded in the deliverable
    //   - When new fonts are loaded in the client.
    useEffect(() => {
        const containerEl = containerRef.current;
        if (!containerEl) {
            return;
        }
        const debouncedCheckForOverflow = debounce(checkForOverflow, 25, true);
        // Observer container resize and re-check for overflow
        const resizeObserver = new ResizeObserver(debouncedCheckForOverflow);
        const readyForScaleEvents = () => {
            setIsReady(true);
            // Note: this will fire the callback immediately,
            // as well as any time `containerEl` changes size.
            resizeObserver.observe(containerEl);
            // We are 100% ready now, so no need to keep listening…
            document.removeEventListener('renderRequested', readyForScaleEvents);
            // …Now, we check for overflow whenever we receive a `renderRequested`.
            document.addEventListener('renderRequested', debouncedCheckForOverflow);
            // debug('useScale: %cattach', 'color: green');
        };
        // Avoid layout shifts. Only fire our scale listener if we are in-editor or
        // already have been told that we are ready (via the `renderRequested` event).
        if (inEditor || isReady) {
            // debug('%cready', 'color: teal');
            readyForScaleEvents();
        }
        else {
            // debug('%cwaiting', 'color: teal');
            document.addEventListener('renderRequested', readyForScaleEvents);
        }
        return () => {
            document.removeEventListener('renderRequested', readyForScaleEvents);
            document.removeEventListener('renderRequested', debouncedCheckForOverflow);
            if (containerEl) {
                resizeObserver.unobserve(containerEl);
            }
            // debug('useScale: %cdetach', 'color: red');
        };
    }, [containerRef, checkForOverflow, isReady, inEditor]);
    return [scale, checkForOverflow];
}
/**
 * Clone a hidden version of an HTMLElement
 * that can be used to calculate for overflow.
 */
export function createOverFlowRuler(element, restStateStyles) {
    const ruler = element.cloneNode(true);
    ruler.id = Math.random()
        .toString(36)
        .substring(7);
    const parent = element.parentElement || document.body;
    css(ruler, Object.assign(Object.assign({}, restStateStyles), { display: 'block', opacity: 0, overflow: 'hidden', transform: '' }));
    parent.appendChild(ruler);
    const unmountRuler = () => parent.removeChild(ruler);
    return [ruler, unmountRuler];
}
